import { Component, EventEmitter, Input, OnInit, Output, inject, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { ActivityService } from '../../activityServices/activity.service';
import { AuthService } from 'projects/patient/src/app/core/auth/services/auth.service';
import { ActivityType, LoggedActivity, CreateActivityRequest } from '../../activity-cosnt/activity-model';

@Component({
  selector: 'patient-activity-log-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, DatePickerModule, ButtonModule],
  templateUrl: './activity-log-modal.component.html',
  styleUrl: './activity-log-modal.component.css',
})
export class ActivityLogModalComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() date: Date = new Date();
  @Input() activityToEdit: LoggedActivity | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() activityLogged = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue === true) {
      this.initForm();
      this.fetchLoggedActivitiesForDate();
    }
  }

  initForm() {
    if (this.activityToEdit) {
      // Edit mode: pre-fill details from activityToEdit
      const match = this.activityTypes.find(
        (act) => act.name.toLowerCase() === this.activityToEdit!.name.toLowerCase()
      );
      if (match) {
        this.selectedActivity = match;
      } else {
        this.selectedActivity = null;
      }

      const duration = this.activityToEdit.duration;
      if (duration === 10 || duration === 20 || duration === 30) {
        this.selectedDurationOption = duration;
        this.activityDurationDate = new Date(new Date().setHours(0, duration, 0, 0));
      } else {
        this.selectedDurationOption = 'custom';
        this.customMinutes = duration;
      }
    } else {
      // Log mode: default values
      const defaultAct = this.activityTypes.find((act) => act.is_default);
      if (defaultAct) {
        this.selectedActivity = defaultAct;
      } else if (this.activityTypes.length > 0) {
        this.selectedActivity = this.activityTypes[0];
      } else {
        this.selectedActivity = null;
      }

      this.selectedDurationOption = 30;
      this.activityDurationDate = new Date(new Date().setHours(0, 30, 0, 0));
    }
  }

  private activityService = inject(ActivityService);
  private authService = inject(AuthService);

  patientId: string = '';
  searchQuery: string = '';
  selectedActivity: ActivityType | null = null;

  // Pre-fill timepicker to 00:30 (representing 30 minutes duration)
  activityDurationDate: Date = new Date(new Date().setHours(0, 30, 0, 0));

  activityTypes: ActivityType[] = [];
  isLoading: boolean = false;

  // Categories list and selection
  selectedCategoryId: string = 'all';
  categories: Array<{ id: string; name: string }> = [
    { id: 'all', name: 'All' },
    { id: 'cardio', name: 'Cardio' },
    { id: 'strength', name: 'Strength' },
    { id: 'lifestyle', name: 'Lifestyle' },
    { id: 'sports', name: 'Sports' },
    { id: 'sports', name: 'Sports' },
    { id: 'sports', name: 'Sports' },
    { id: 'sports', name: 'Sports' },
  ];

  // Quick duration selection
  selectedDurationOption: number | 'custom' = 30;
  customMinutes: number = 45;

  ngOnInit(): void {
    this.getPatientId();
    this.loadCategories();
    this.loadActivityTypes();
  }

  getPatientId() {
    this.authService.getUserProfile().subscribe((res) => {
      if (res?.PatientId) {
        this.patientId = res.PatientId;
        this.fetchLoggedActivitiesForDate();
      }
    });
  }

  loggedActivitiesForDate: LoggedActivity[] = [];

  fetchLoggedActivitiesForDate() {
    if (!this.patientId || !this.date) return;

    const localDate = this.date || new Date();
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    this.activityService.getActivityAnalyticByDate(this.patientId, dateStr).subscribe({
      next: (res) => {
        if (res && res.data && res.data.logged_activities) {
          this.loggedActivitiesForDate = res.data.logged_activities;
        } else {
          this.loggedActivitiesForDate = [];
        }
      },
      error: (err) => {
        console.error('Error fetching logged activities for modal validation:', err);
        this.loggedActivitiesForDate = [];
      }
    });
  }

  get totalLoggedDurationExcludingCurrent(): number {
    let total = 0;
    const editId = this.activityToEdit ? this.activityToEdit.id : null;
    for (const act of this.loggedActivitiesForDate) {
      if (editId && act.id === editId) {
        continue;
      }
      total += act.duration;
    }
    return total;
  }

  get validationErrorMessage(): string | null {
    if (this.selectedDurationOption === 'custom' && (!this.customMinutes || this.customMinutes <= 0)) {
      return 'Please enter a valid activity duration in minutes.';
    }
    const mins = this.durationInMinutes;
    if (mins <= 0) {
      return 'Please enter a valid activity duration.';
    }
    if (mins > 300) {
      return 'Activity duration cannot exceed 300 minutes (5 hours).';
    }
    const totalWithCurrent = this.totalLoggedDurationExcludingCurrent + mins;
    if (totalWithCurrent > 300) {
      const remaining = 300 - this.totalLoggedDurationExcludingCurrent;
      if (remaining <= 0) {
        return `You have already logged ${this.totalLoggedDurationExcludingCurrent} minutes of activity for this date. The daily limit is 300 minutes.`;
      }
      return `You have already logged ${this.totalLoggedDurationExcludingCurrent} minutes of activity for this date. You can only log up to ${remaining} more minutes.`;
    }
    return null;
  }

  loadCategories() {
    this.activityService.getCategory().subscribe({
      next: (res) => {
        if (res && res.data && res.data.activity_categories) {
          this.categories = [
            { id: 'all', name: 'All' },
            ...res.data.activity_categories.map((cat) => ({ id: cat.id, name: cat.name })),
          ];
        }
      },
      error: (err) => {
        console.error('Error fetching categories, using fallback static data', err);
      },
    });
  }

  normalizeActivityTypes(types: ActivityType[]): ActivityType[] {
    return types.map((act) => {
      if (act.category_id && act.category_name) {
        return act;
      }

      const name = act.name.toLowerCase();
      let catId = 'lifestyle';
      let catName = 'Lifestyle';

      if (
        name.includes('walk') ||
        name.includes('run') ||
        name.includes('cycl') ||
        name.includes('bike') ||
        name.includes('swim') ||
        name.includes('treadmill') ||
        name.includes('cardio')
      ) {
        catId = 'cardio';
        catName = 'Cardio';
      } else if (
        name.includes('gym') ||
        name.includes('strength') ||
        name.includes('weight') ||
        name.includes('push') ||
        name.includes('pilates') ||
        name.includes('plank') ||
        name.includes('squat')
      ) {
        catId = 'strength';
        catName = 'Strength';
      } else if (name.includes('yoga') || name.includes('meditat') || name.includes('stretch')) {
        catId = 'lifestyle';
        catName = 'Lifestyle';
      } else if (
        name.includes('ball') ||
        name.includes('cricket') ||
        name.includes('tennis') ||
        name.includes('badminton') ||
        name.includes('sport') ||
        name.includes('play')
      ) {
        catId = 'sports';
        catName = 'Sports';
      }

      return {
        ...act,
        category_id: catId,
        category_name: catName,
      };
    });
  }

  loadActivityTypes() {
    this.isLoading = true;
    this.activityService.getActivityTypes().subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res && res.data && res.data.activity_types) {
          this.activityTypes = this.normalizeActivityTypes(res.data.activity_types);
          this.initForm();
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error fetching activity types:', err);
      },
    });
  }

  get filteredActivityTypes(): ActivityType[] {
    let filtered = this.activityTypes;

    // Filter by selected category first
    if (this.selectedCategoryId !== 'all') {
      filtered = filtered.filter((act) => act.category_id === this.selectedCategoryId);
    }

    if (!this.searchQuery.trim()) {
      return filtered;
    }
    return filtered.filter((act) =>
      act.name.toLowerCase().includes(this.searchQuery.toLowerCase()),
    );
  }

  selectActivity(activity: ActivityType) {
    this.selectedActivity = activity;
  }

  selectCategory(catId: string) {
    this.selectedCategoryId = catId;
  }

  setDuration(mins: number) {
    this.selectedDurationOption = mins;
    this.activityDurationDate = new Date(new Date().setHours(0, mins, 0, 0));
  }

  setCustomDuration() {
    this.selectedDurationOption = 'custom';
  }

  preventSymbols(event: KeyboardEvent) {
    const invalidChars = ['-', '+', 'e', 'E', '.', ','];
    if (invalidChars.includes(event.key)) {
      event.preventDefault();
    }
  }

  preventPasteSymbols(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData?.getData('text');
    if (pastedText && !/^\d+$/.test(pastedText)) {
      event.preventDefault();
    }
  }

  sanitizeInput() {
    if (this.customMinutes !== null && this.customMinutes !== undefined) {
      const strVal = String(this.customMinutes);
      const cleanVal = strVal.replace(/[^\d]/g, '');
      this.customMinutes = cleanVal ? parseInt(cleanVal, 10) : 0;
    }
  }

  closeModal() {
    this.visibleChange.emit(false);
  }

  get durationInMinutes(): number {
    if (this.selectedDurationOption === 'custom') {
      return this.customMinutes || 0;
    }
    return this.selectedDurationOption;
  }

  logActivity() {
    if (!this.selectedActivity || !this.patientId) return;

    let dateStr = '';
    let timeStr = '';

    if (this.activityToEdit) {
      dateStr = this.activityToEdit.date;
      timeStr = this.activityToEdit.time;
    } else {
      // Timezone-safe local YYYY-MM-DD
      const localDate = this.date || new Date();
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;

      // Get current local time in HH:mm:ss format
      const now = new Date();
      timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    }

    const payload: CreateActivityRequest = {
      log_id: this.activityToEdit ? this.activityToEdit.id : null,
      patient_id: this.patientId,
      date: dateStr,
      time: timeStr,
      activity_id: this.selectedActivity.id,
      activity_name: this.selectedActivity.name,
      duration: this.durationInMinutes,
      created_by: this.patientId,
    };

    this.activityService.logActivity(payload).subscribe({
      next: (res) => {
        console.log(
          this.activityToEdit ? 'Activity updated successfully:' : 'Activity logged successfully:',
          res
        );
        this.activityLogged.emit();
        this.closeModal();
      },
      error: (err) => {
        console.error(
          this.activityToEdit ? 'Error updating activity:' : 'Error logging activity:',
          err
        );
      },
    });
  }

  makeDefaultActivity(activity: ActivityType, event: Event) {
    event.stopPropagation(); // prevent selecting the row as logged activity if not wanted, or trigger both
    if (!this.patientId) return;

    const payload = {
      patient_id: this.patientId,
      activity_id: activity.id,
      is_default: true,
      updated_by: this.patientId,
    };

    this.activityService.setDefaultActivity(payload).subscribe({
      next: (res) => {
        console.log('Default activity set successfully:', res);

        // Update local state is_default properties
        this.activityTypes.forEach((act) => {
          act.is_default = act.id === activity.id;
        });

        // Auto select the new default activity as the logged activity
        this.selectedActivity = activity;
      },
      error: (err) => {
        console.error('Error setting default activity:', err);
      },
    });
  }
}
